import type { AccountSession } from "./accountTypes";
import { withConnectionAuth } from "../app/connection/auth";
import { accountApiBase } from "../app/connection/resolveProfile";
import { devBypassSession, isDevBypassEnabled } from "./devBypass";
import { invalidateAccountAuthWork } from "./accountAuthEpoch";

/**
 * better-auth's client parses JSON responses through its own reviver (see
 * `getClientConfig`'s custom `jsonParser`), which turns ISO-date-shaped
 * string values into real `Date` objects — confirmed directly against the
 * installed client (`session.expiresAt` comes back as a `Date` instance, not
 * a string, even though the wire payload is JSON). Every other consumer of
 * `AccountSession.expiresAt` in this codebase expects a plain ISO string
 * (`Date.parse(expiresAt)` / `new Date(expiresAt)` call sites), so normalize
 * back to a string here rather than leaking the client's reviving behavior.
 */
function toIsoString(value: unknown): string | null {
  if (value instanceof Date) return value.toISOString();
  if (typeof value === "string") return value;
  return null;
}

/**
 * Normalize better-auth's `getSession()` payload into this app's own
 * `AccountSession` shape. Sign-in responses can be partial; callers resolve
 * the complete cookie session through `fetchAccountSession()` instead.
 */
export function normalizeAccountSessionData(
  data:
    | {
        user?: {
          id?: string | null;
          email?: string | null;
          name?: string | null;
          image?: string | null;
        } | null;
        session?: { expiresAt?: unknown } | null;
      }
    | null
    | undefined,
): AccountSession | null {
  if (!data?.user?.id) return null;
  return {
    user: {
      id: data.user.id,
      email: data.user.email ?? null,
      name: data.user.name ?? null,
      image: data.user.image ?? null,
    },
    expiresAt: toIsoString(data.session?.expiresAt),
  };
}

/**
 * The session read is a plain GET, not better-auth's client: the first screen
 * needs only this, and the client (with its passkey and OTP plugins) loads
 * when a sign-in action runs. Same semantics: 401 or a null 200 body is
 * signed out, any other failure throws.
 */
function requestAccountSession(): Promise<Response> {
  return fetch(`${accountApiBase()}/api/auth/get-session`, withConnectionAuth({
    method: "GET",
    headers: { Accept: "application/json" },
    cache: "no-store",
  }));
}

let earlySession: Promise<Response> | null = null;

/**
 * Start the session read at boot, before the app mounts, so it runs alongside
 * the code and dictionary downloads instead of after them. The first
 * `fetchAccountSession()` takes it; later calls read again.
 */
export function prefetchAccountSession(): void {
  if (earlySession || isDevBypassEnabled()) return;
  earlySession = requestAccountSession();
  earlySession.catch(() => {});
}

export async function fetchAccountSession(): Promise<AccountSession | null> {
  // Local-dev auth bypass (see devBypass.ts): resolve the scratch identity
  // with no network call. better-auth's own /get-session would only ever say
  // "no session" — the bypass is not a cookie session, the API accepts the
  // token header on its own routes instead.
  if (isDevBypassEnabled()) return devBypassSession();
  const pending = earlySession;
  earlySession = null;
  const response = await (pending ?? requestAccountSession());
  // A signed-out caller is a normal, expected outcome, not a failure.
  if (response.status === 401) return null;
  if (!response.ok) throw new Error(`Account session fetch failed: ${response.status}`);
  // A signed-out caller may also come back as a plain 200 with a null body
  // (better-auth's real `/get-session` endpoint does this) — normalize
  // handles that the same way as an absent user.
  return normalizeAccountSessionData(await response.json().catch(() => null));
}

export async function signOutAccount(): Promise<void> {
  invalidateAccountAuthWork();
  // In bypass mode there is no cookie session to destroy; reload re-applies
  // the bypass, so signing out is a no-op (restart dev without the token to
  // truly sign out). Calling through would only surface a confusing error.
  if (isDevBypassEnabled()) return;
  earlySession = null;
  const { accountAuthClient } = await import("./accountAuthClient");
  await accountAuthClient.signOut();
}
